import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSpinner } from 'react-icons/fa';
import PlotViewer from '../PlotViewer';
import { PlottingInterfaceProps } from './types';
import { usePlottingInterface } from './hooks';
import { PlotSettingsSection } from './PlotSettingsSection';
import { VariableSelectionSection } from './VariableSelectionSection';

/**
 * PlottingInterface Component
 *
 * Replicates plotting interface from original training_original.py
 * Allows selection of input, output, and forecast data for plotting
 */
export const PlottingInterface: React.FC<PlottingInterfaceProps> = memo((props) => {
  const { t } = useTranslation('training');

  const {
    plotConfig,
    isLoading,
    isLoadingVariables,
    error,
    generatedPlot,
    showPlot,
    handleInputVariableChange,
    handleTimeComponentChange,
    handleOutputVariableChange,
    handleForecastVariableChange,
    handleNumSubplotsChange,
    handleXAxisChange,
    handleYAxisFormatChange,
    handleYAxisConfigChange,
    handleGeneratePlot,
    closePlot
  } = usePlottingInterface(props);

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground tracking-tight">
          {t('training.plotting.title')}
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t('training.plotting.subtitle')}
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* Plot Settings */}
        <PlotSettingsSection
          plotConfig={plotConfig}
          onNumSubplotsChange={handleNumSubplotsChange}
          onXAxisChange={handleXAxisChange}
          onYAxisFormatChange={handleYAxisFormatChange}
          onYAxisConfigChange={handleYAxisConfigChange}
        />

        {/* Input Data Selection */}
        <div>
          <h3 className="text-sm font-semibold text-foreground tracking-tight mb-3">
            {t('training.plotting.plotInputData')}
          </h3>
          <div className="bg-muted/30 rounded-lg border border-border/60 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Regular Input Variables */}
              <VariableSelectionSection
                title={t('training.plotting.inputVariables')}
                variables={plotConfig.inputVariables}
                onVariableChange={handleInputVariableChange}
                emptyMessage={t('training.plotting.noInputVariables')}
                keyPrefix="input"
                isLoading={isLoadingVariables}
              />

              {/* TIME Components */}
              <VariableSelectionSection
                title={t('training.plotting.timeComponents')}
                variables={plotConfig.timeComponents}
                onVariableChange={handleTimeComponentChange}
                emptyMessage={t('training.plotting.noTimeComponents')}
                keyPrefix="time"
                isLoading={isLoadingVariables}
              />
            </div>
          </div>
        </div>

        {/* Output and Forecast Data */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Output Data */}
          <div>
            <h3 className="text-sm font-semibold text-foreground tracking-tight mb-3">
              {t('training.plotting.plotOutputData')}
            </h3>
            <div className="bg-muted/50 rounded-lg border border-border p-4">
              <VariableSelectionSection
                title=""
                variables={plotConfig.outputVariables}
                onVariableChange={handleOutputVariableChange}
                emptyMessage={t('training.plotting.noOutputVariables')}
                keyPrefix="output"
                isLoading={isLoadingVariables}
              />
            </div>
          </div>

          {/* Forecast Data */}
          <div>
            <h3 className="text-sm font-semibold text-foreground tracking-tight mb-3">
              {t('training.plotting.plotForecastData')}
            </h3>
            <div className="bg-muted/50 rounded-lg border border-border p-4">
              <VariableSelectionSection
                title=""
                variables={plotConfig.forecastVariables}
                onVariableChange={handleForecastVariableChange}
                emptyMessage={t('training.plotting.noForecastVariables')}
                keyPrefix="forecast"
                isLoading={isLoadingVariables}
              />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200/80 rounded-lg">
            <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-red-500 text-[10px] font-bold">!</span>
            </div>
            <p className="text-sm text-red-600 leading-relaxed">{error}</p>
          </div>
        )}

        {/* Generate Plot Button */}
        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={handleGeneratePlot}
            disabled={isLoading}
            className="group flex items-center gap-2.5 px-8 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground transition-all duration-150 hover:shadow-md active:scale-[0.98]"
          >
            {isLoading ? (
              <>
                <FaSpinner className="animate-spin text-xs" />
                <span>{t('training.plotting.generating')}</span>
              </>
            ) : (
              <span>{t('training.plotting.generatePlot')}</span>
            )}
          </button>
        </div>
      </div>

      {/* Plot Viewer */}
      <PlotViewer
        plotData={generatedPlot || undefined}
        isVisible={showPlot}
        onClose={closePlot}
      />
    </div>
  );
});

PlottingInterface.displayName = 'PlottingInterface';

export default PlottingInterface;
